<script setup lang="ts">
import EstateListingServices from '../listing/services/EstateListingServices.vue'
import OfficeListingFilter from '../listing/filter/OfficeListingFilter.vue'
import RetailListingFilter from '../listing/filter/RetailListingFilter.vue'
import WarehouseListingFilter from '../listing/filter/WarehouseListingFilter.vue'
import EstateFilterBase from '../listing/filter/EstateFilterBase.vue'
import EstateListingMap from './EstateListingMap.vue'
import EstateListingItems from './EstateListingItems.vue'
import { useEstateListingData } from '~/composables/api/useEstateListingData'
import type {
  EstateListingEssentialResponse,
  EstateListingResponse,
} from '~/types/api/EstateListing'
import TailoredOfferBanner from '~/components/global/TailoredOfferBanner.vue'
import { useEstateListingMap } from '~/composables/estateListingMap/useEstateListingMap'
import { usePaginationState } from '~/composables/pagination/usePaginationState'
import Icon from '~/components/global/Icon.vue'
import { useFilterState } from '~/composables/filter/useFilterState'
import type { EstateType } from '~/types/EstateType'

const props = defineProps<{
  estateType: EstateType
}>()

const listingItemsRef = ref<HTMLElement | null>(null)
const paginationState = usePaginationState({
  type: 'estate',
})
const filterState = useFilterState({
  paginationState,
})
const estateListingMap = useEstateListingMap({
  filterState,
})

let controller: AbortController
const officesResponse = useAsyncData<EstateListingResponse>(
  async () => {
    controller?.abort?.()
    controller =
      typeof AbortController !== 'undefined'
        ? new AbortController()
        : ({} as AbortController)

    return await useEstateListingData({
      paginationState,
      filterState,
      estateType: props.estateType,
      abortController: controller,
    })
  },
  {
    lazy: true,
    server: false,
    immediate: false,
  }
)

watchEffect(() => {
  if (officesResponse.pending.value) return

  paginationState.actions.setResponse(officesResponse)
})

let essentialController: AbortController
const officesEssentialResponse = useAsyncData<EstateListingEssentialResponse>(
  async () => {
    essentialController?.abort?.()
    essentialController =
      typeof AbortController !== 'undefined'
        ? new AbortController()
        : ({} as AbortController)

    const data = await useEstateListingData({
      estateType: props.estateType,
      params: {
        page: 1,
        limit: 9999,
        essential: 1,
        latitude_from: null,
        latitude_to: null,
        longitude_from: null,
        longitude_to: null,
      },
      filterState,
      abortController: essentialController,
    })

    return data
  },
  {
    lazy: true,
    server: false,
  }
)

filterState.actions.addRefreshFunction(
  'offices_essential_response',
  officesEssentialResponse.refresh,
  ['latitude_from', 'latitude_to', 'longitude_from', 'longitude_to']
)

onMounted(() => {
  if (window.matchMedia('screen and (min-width: 1199.99px)').matches) {
    officesResponse.execute()

    filterState.actions.addRefreshFunction(
      'offices_response',
      officesResponse.refresh
    )
  }

  window
    .matchMedia('screen and (min-width: 1199.99px)')
    .addEventListener('change', (e) => {
      filterState.actions.deleteRefreshFunction('offices_response')

      if (!e.matches) {
        return
      }

      officesResponse.execute()

      filterState.actions.addRefreshFunction(
        'offices_response',
        officesResponse.refresh
      )
    })
})

onUnmounted(() => {
  officesResponse.data.value = null
  officesEssentialResponse.data.value = null

  estateListingMap.onUnmounted()
})

const defaultListingLink = computed(() => {
  try {
    const { query } = useRoute()
    const { filter } = query
    const parsedFilter = JSON.parse(
      decodeURIComponent(filter?.toString() || '') || '{}'
    )
    const filterQuery = {
      'scroll-to-hash': 'listing-scroll-hook',
    } as Record<string, string>

    delete parsedFilter.latitude_from
    delete parsedFilter.latitude_to
    delete parsedFilter.longitude_from
    delete parsedFilter.longitude_to

    if (Object.keys(parsedFilter).length > 0) {
      filterQuery.filter = encodeURIComponent(JSON.stringify(parsedFilter))
    }

    return getEstateListingLink({
      estateType: props.estateType,
      query: filterQuery,
    })
  } catch (error) {}

  return getEstateListingLink({
    estateType: props.estateType,
    query: {
      'scroll-to-hash': 'listing-scroll-hook',
    },
  })
})

const currentFilterComponent = (() => {
  if (props.estateType === 'office') {
    return OfficeListingFilter
  }

  if (props.estateType === 'retail') {
    return RetailListingFilter
  }

  if (props.estateType === 'warehouse') {
    return WarehouseListingFilter
  }

  return OfficeListingFilter
})()
</script>

<template>
  <div class="c-estate-listing-with-map">
    <div class="c-estate-listing-with-map__filter">
      <EstateFilterBase
        :estate-type="estateType"
        :offices-response="officesEssentialResponse.data.value"
        :offices-pending="officesEssentialResponse.pending.value"
        :filter-state="filterState"
        :is-on-map="true"
      >
        <component
          :is="currentFilterComponent"
          v-if="officesEssentialResponse.data.value"
          :filter-state="filterState"
          :offices-response="officesEssentialResponse.data.value"
        />
      </EstateFilterBase>
    </div>

    <div class="c-estate-listing-with-map__main">
      <div class="sc-button">
        <IButton class="has-icon" color="primary" :to="defaultListingLink">
          <Icon name="document" />

          <span class="_sm:visible">{{
            $t('pages.estateListing.viewInListingLong')
          }}</span>
          <span class="_sm:hidden">{{
            $t('pages.estateListing.viewInListing')
          }}</span>
        </IButton>
      </div>

      <EstateListingMap
        :key="estateType"
        :offices-essential-response="officesEssentialResponse"
        :estate-type="estateType"
        :estate-listing-map="estateListingMap"
        :filter-state="filterState"
      />

      <EstateListingItems
        ref="listingItemsRef"
        :key="estateType"
        :offices-response="officesResponse"
        :estate-type="estateType"
        :estate-listing-map="estateListingMap"
        :pagination-state="paginationState"
        :filter-state="filterState"
      />
    </div>

    <EstateListingServices
      class="c-estate-listing-with-map__services"
      :show-title="true"
      :show-border-top="false"
    />

    <TailoredOfferBanner class="c-estate-listing-with-map__banner" />
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-estate-listing-with-map {
  &__main {
    display: grid;
    grid-template-columns: 1fr 720px;
    margin-bottom: 72px;
    height: calc(90svh - var(--header-height));
    border-bottom: 1px solid var(--color-border);
    position: relative;

    .sc-button {
      position: absolute;
      z-index: 10;
      top: 20px;
      left: 20px;

      @include breakpoint-down('sm') {
        top: unset;
        bottom: 20px;
        left: 0;
        right: 0;
        margin: auto;
        width: fit-content;
      }

      .button {
        --button--padding-left: 28px;
        --button--padding-right: 28px;
        min-height: 62px;
        font-weight: 500;

        @include breakpoint-down('sm') {
          width: 100%;
        }
        @include breakpoint-down('sm') {
          --button--font-size: 14px;
          --button--padding-left: 16px;
          --button--padding-right: 16px;
          --button--icon--font-size: 20px;

          min-height: 47px;
        }
      }
    }

    @include breakpoint-down('xl') {
      grid-template-columns: 1fr 600px;
    }

    @include breakpoint-down('lg') {
      grid-template-columns: 1fr;
    }
  }

  &__services {
    margin-bottom: 72px;
  }

  &__banner {
    margin-bottom: 100px;
  }
}
</style>
